import TomSelect from "tom-select";

function isTomSelectElement(input) {
  return !!input && typeof input.tagName === 'string' && typeof input.value === 'string';
}

function hasTomSelectValue(value) {
  return Array.isArray(value) ? value.length > 0 : value != null && value !== '';
}

function firstTomSelectValue(value) {
  return Array.isArray(value) ? value[0] : value;
}

function selectedTomSelectText(tomSelect) {
  const value = firstTomSelectValue(tomSelect.getValue());
  if (!hasTomSelectValue(value)) return '';

  const option = tomSelect.options?.[value];
  const labelField = tomSelect.settings?.labelField;
  const label = labelField ? option?.[labelField] : null;
  return typeof label === 'string' && label.length > 0 ? label : String(value);
}

function clearStaleTomSelectSelection(tomSelect, query) {
  const typedText = query?.trim();
  if (!typedText) return;

  const selectedText = selectedTomSelectText(tomSelect).trim();
  if (!selectedText || selectedText === typedText) return;

  tomSelect.clear(true);
  tomSelect.setTextboxValue(query);
  tomSelect.refreshOptions(false);
}

function syncCommittedTomSelectSelection(input, tomSelect) {
  const value = firstTomSelectValue(tomSelect.getValue());
  const selectedText = selectedTomSelectText(tomSelect).trim();

  if (hasTomSelectValue(value) && selectedText) {
    input.dataset.tomSelectCommittedValue = String(value);
    input.dataset.tomSelectCurrentQuery = selectedText;
    input.value = String(value);
  } else {
    delete input.dataset.tomSelectCommittedValue;
  }
}

function syncPendingTomSelectText(input) {
  const tomSelect = input?.tomselect;
  if (!tomSelect) return;

  const pendingText = tomSelect.control_input?.value?.trim();
  const storedQuery = input.dataset.tomSelectCurrentQuery?.trim();
  const committedValue = input.dataset.tomSelectCommittedValue?.trim();
  const selectedValue = tomSelect.getValue();

  if (pendingText) {
    input.value = pendingText;
    return;
  }

  if (committedValue) {
    input.value = committedValue;
    return;
  }

  if (storedQuery) {
    input.value = storedQuery;
    return;
  }

  if (!hasTomSelectValue(selectedValue)) {
    if (!input.value?.trim()) input.value = '';
  }
}

function finalizeSingleTomSelectSelection(tomSelect, value) {
  if (!hasTomSelectValue(value) || !tomSelect) return;

  requestAnimationFrame(() => {
    tomSelect.close(false);
    tomSelect.blur();
  });
}

function disableTomSelectRemoteQueryCache(tomSelect) {
  if (!tomSelect?.settings?.load) return;

  tomSelect.canLoad = function() {
    return true;
  };
}

function bindTomSelectSubmitSync(input) {
  const form = input.closest('form');
  if (!form || input.dataset.tomSelectSubmitSyncBound === 'true') return;

  form.addEventListener('submit', () => {
    syncPendingTomSelectText(input);
  }, { capture: true });

  input.dataset.tomSelectSubmitSyncBound = 'true';
}

function normalizeTomSelectClipboardValue(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function selectedTomSelectClipboardValue(input, tomSelect) {
  const committedValue = input?.dataset.tomSelectCommittedValue?.trim();
  if (committedValue) return committedValue;

  const selectedValue = firstTomSelectValue(tomSelect?.getValue());
  return hasTomSelectValue(selectedValue) ? String(selectedValue).trim() : '';
}

async function resolvePastedCardSelection(query, input) {
  const valueField = input?.tomselect?.settings?.valueField;
  const isCardId = valueField === 'id';
  const endpoint = isCardId ? '/autocomplete' : '/autocomplete_card';
  const response = await fetch(`${endpoint}?term=${encodeURIComponent(query)}`);
  if (!response.ok) return null;

  const items = await response.json();
  const normalizedQuery = normalizeTomSelectClipboardValue(query);

  if (isCardId) {
    return items.find(item => normalizeTomSelectClipboardValue(item?.id) === normalizedQuery) || null;
  }

  const match = items.find(item => normalizeTomSelectClipboardValue(item) === normalizedQuery);
  return match ? { value: match, label: match } : null;
}

function writeTomSelectClipboardText(value, focusTarget = null) {
  if (!value) return false;

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);

  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch (_error) {
    copied = false;
  } finally {
    textarea.remove();
    focusTarget?.focus?.();
  }

  if (copied) return true;

  try {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value);
      return true;
    }
  } catch (_error) {
    return false;
  }

  return false;
}

function sourceTomSelectInputFromControl(controlInput) {
  const wrapper = controlInput?.closest('.ts-wrapper');
  const field = wrapper?.parentElement;
  const localInput = field?.querySelector('input.ts-hidden-accessible, textarea.ts-hidden-accessible, select.ts-hidden-accessible');
  if (localInput?.tomselect) return localInput;

  const controlId = controlInput?.id;
  if (typeof controlId !== 'string' || !controlId.endsWith('-ts-control')) return null;

  const sourceInput = document.getElementById(controlId.replace(/-ts-control$/, ''));
  if (!sourceInput?.tomselect) return null;

  return sourceInput;
}

function tomSelectClipboardContextForNode(node) {
  const controlInput = node && typeof node.matches === 'function' && node.matches('.ts-control input[role="combobox"]') ?
    node :
    document.activeElement;
  if (!controlInput || typeof controlInput.matches !== 'function') return null;
  if (!controlInput.matches('.ts-control input[role="combobox"]')) return null;

  const input = sourceTomSelectInputFromControl(controlInput);
  const tomSelect = input?.tomselect;
  if (!input || !tomSelect) return null;

  return { input, tomSelect, controlInput };
}

async function applyTomSelectPastedText(input, tomSelect, pastedText) {
  pastedText = pastedText?.trim();
  if (!pastedText) return;

  input.dataset.tomSelectCurrentQuery = pastedText;
  tomSelect.clear(true);
  tomSelect.setTextboxValue(pastedText);

  const valueField = tomSelect.settings?.valueField || 'value';
  const labelField = tomSelect.settings?.labelField || 'label';

  if (!tomSelect.settings?.load) {
    const normalizedPaste = normalizeTomSelectClipboardValue(pastedText);
    const match = Object.values(tomSelect.options).find(opt => {
      return normalizeTomSelectClipboardValue(opt[valueField]) === normalizedPaste ||
             normalizeTomSelectClipboardValue(opt[labelField]) === normalizedPaste;
    });
    if (match) {
      tomSelect.setValue(match[valueField]);
    }
    return;
  }

  try {
    const match = await resolvePastedCardSelection(pastedText, input);
    if (match) {
      tomSelect.addOption(match);
      tomSelect.setValue(match[valueField]);
      return;
    }
  } catch (_error) {
    // Fall through to query-mode paste when exact resolution fails.
  }

  tomSelect.load(pastedText);
  tomSelect.refreshOptions(true);
}

function installTomSelectClipboardHandlers() {
  if (document.documentElement.dataset.tomSelectClipboardHandlersBound === 'true') return;

  document.addEventListener('keydown', event => {
    const context = tomSelectClipboardContextForNode(event.target);
    if (!context) return;
    if ((event.ctrlKey || event.metaKey) !== true || event.altKey) return;
    const key = event.key.toLowerCase();
    const { input, tomSelect, controlInput } = context;

    if (key === 'c') {
      if (controlInput.value?.trim()) return;
      if (controlInput.selectionStart !== controlInput.selectionEnd) return;

      const selectedValue = selectedTomSelectClipboardValue(input, tomSelect);
      if (!selectedValue) return;

      event.preventDefault();
      writeTomSelectClipboardText(selectedValue, controlInput);
      return;
    }

    if (key !== 'v') return;
  }, true);

  document.addEventListener('copy', event => {
    const context = tomSelectClipboardContextForNode(event.target);
    if (!context) return;

    const selectedValue = selectedTomSelectClipboardValue(context.input, context.tomSelect);
    if (!selectedValue || !event.clipboardData) return;

    event.preventDefault();
    event.clipboardData.setData('text/plain', selectedValue);
  }, true);

  document.addEventListener('paste', event => {
    const context = tomSelectClipboardContextForNode(event.target);
    if (!context) return;

    const pastedText = event.clipboardData?.getData('text/plain')?.trim();
    if (!pastedText) return;

    event.preventDefault();
    const { input, tomSelect } = context;
    void applyTomSelectPastedText(input, tomSelect, pastedText);
  }, true);

  document.documentElement.dataset.tomSelectClipboardHandlersBound = 'true';
}

export function bindTomSelectClipboardSync(input) {
  if (!input?.tomselect || input.dataset.tomSelectClipboardSyncBound === 'true') return;
  installTomSelectClipboardHandlers();

  input.dataset.tomSelectClipboardSyncBound = 'true';
}

function submitTomSelectCurrentText(input) {
  console.log('[autocomplete-submit-current] submitTomSelectCurrentText:start', {
    name: input?.name,
    value: input?.value,
    controlValue: input?.tomselect?.control_input?.value,
  });
  syncPendingTomSelectText(input);
  console.log('[autocomplete-submit-current] submitTomSelectCurrentText:after-sync', {
    name: input?.name,
    value: input?.value,
    controlValue: input?.tomselect?.control_input?.value,
  });

  const form = input.closest('form');
  if (!form) return;

  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit();
  } else {
    form.submit();
  }
}

function bindTomSelectQueryTracking(input) {
  const tomSelect = input?.tomselect;
  const controlInput = tomSelect?.control_input;
  if (!controlInput || input.dataset.tomSelectQueryTrackingBound === 'true') return;

  const updateCurrentQuery = source => {
    const query = controlInput.value;
    if (query) {
      input.dataset.tomSelectCurrentQuery = query;
      if (input.dataset.tomSelectCommittedValue && input.dataset.tomSelectCommittedValue !== query) {
        delete input.dataset.tomSelectCommittedValue;
      }
    } else {
      delete input.dataset.tomSelectCurrentQuery;
    }
    console.log('[autocomplete-submit-current] query-update', {
      source,
      name: input?.name,
      inputValue: input?.value,
      controlValue: controlInput?.value,
      storedQuery: input.dataset.tomSelectCurrentQuery,
      committedValue: input.dataset.tomSelectCommittedValue,
    });
  };

  controlInput.addEventListener('input', () => {
    updateCurrentQuery('input');
  });

  controlInput.addEventListener('keyup', event => {
    if (event.key === 'Enter') return;
    updateCurrentQuery(`keyup:${event.key}`);
  });

  input.dataset.tomSelectQueryTrackingBound = 'true';
}

function bindTomSelectEnterSubmit(input) {
  const tomSelect = input?.tomselect;
  const controlInput = tomSelect?.control_input;
  if (!controlInput || input.dataset.tomSelectEnterSubmitBound === 'true') return;

  // `/q` uses TomSelect on a plain text search field. TomSelect can keep the
  // hidden input value, live textbox text, and selected option out of sync,
  // especially for prepopulated values and fast Enter submits. We track the
  // in-progress query separately and intercept Enter in capture phase so typed
  // text wins when nothing was selected, while committed selections still win
  // on normal submit.
  console.log('[autocomplete-submit-current] bindTomSelectEnterSubmit', {
    name: input?.name,
    initialValue: input?.value,
    controlValue: controlInput?.value,
  });

  bindTomSelectQueryTracking(input);

  controlInput.addEventListener('keydown', event => {
    console.log('[autocomplete-submit-current] keydown', {
      key: event.key,
      name: input?.name,
      inputValue: input?.value,
      controlValue: controlInput?.value,
      storedQuery: input.dataset.tomSelectCurrentQuery,
      selectedText: selectedTomSelectText(tomSelect),
      committedValue: input.dataset.tomSelectCommittedValue,
    });

    if (event.key !== 'Enter' || event.isComposing) return;

    const typedText = (controlInput.value || input.dataset.tomSelectCurrentQuery || '').trim();
    const selectedText = selectedTomSelectText(tomSelect).trim();

    console.log('[autocomplete-submit-current] enter-eval', {
      typedText,
      selectedText,
      shouldIntercept: !!typedText && typedText !== selectedText,
    });

    if (!typedText || typedText === selectedText) return;

    event.preventDefault();
    event.stopPropagation();
    console.log('[autocomplete-submit-current] enter-intercepted', {
      typedText,
      selectedText,
    });
    input.value = typedText;
    tomSelect.close();
    submitTomSelectCurrentText(input);
  }, true);

  input.dataset.tomSelectEnterSubmitBound = 'true';
}

function renderCardItem(item, escape) {
  let left = `<div class="search-card-name">${escape(item.name)}`;
  if (item.card_num) left += `<span class="card-num">#${escape(String(item.card_num))}</span>`;
  let hasFoilTag = false;
  if (item.tags?.length) {
    item.tags.forEach(tag => {
      if (tag.toLowerCase().includes('foil')) hasFoilTag = true;
      left += `<span class="badge badge-variation">${escape(tag)}</span>`;
    });
  }
  if (!hasFoilTag) {
    if (item.finish === 'foil') left += `<span class="badge badge-variation">Foil</span>`;
    else if (item.finish === 'foil_etched') left += `<span class="badge badge-variation">Foil Etched</span>`;
  }
  left += `</div>`;
  const setCode = (item.set_code || '').toLowerCase();
  const unsupported = window.__keyruneUnsupportedSvgMap || {};
  let setIcon;
  if (unsupported[setCode]) {
    setIcon = `<span class="set-symbol set-symbol-svg" style="mask-image: url(${escape(unsupported[setCode])}); -webkit-mask-image: url(${escape(unsupported[setCode])});" title="${escape(item.set || '')}"></span>`;
  } else {
    setIcon = `<i class="ss ss-${escape(setCode)}" title="${escape(item.set || '')}"></i>`;
  }
  const right = `<span class="search-set-name">${setIcon} ${escape(item.set_code || '')}</span>`;
  return `<div class="autocomplete-card-option">${left}${right}</div>`;
}

function buildRemoteCardAutocompleteItem(item) {
  const searchParts = [
    item?.name,
    item?.oracle_name,
    item?.id,
    item?.set,
    item?.set_code,
    item?.card_num,
    item?.finish,
    item?.finish_label,
    ...(Array.isArray(item?.tags) ? item.tags : []),
  ];

  return {
    ...item,
    search_text: buildAutocompleteSearchText(searchParts),
  };
}

function normalizeCardAutocompleteTerm(value) {
  return String(value).replace(/[.'\u2018\u2019]/g, '');
}

function buildAutocompleteSearchText(parts) {
  return parts.filter(Boolean).flatMap(part => {
    const text = String(part);
    const normalized = normalizeCardAutocompleteTerm(text);
    return normalized === text ? [text] : [text, normalized];
  }).join(' ');
}

// Autocomplete for /autocomplete — rich card objects {name, id, card_uuid, set, tags, finish, card_num}
export function initCardIdAutocomplete(input, { submitOnSelect = false } = {}) {
  if (!isTomSelectElement(input)) return null;
  if (input.tomselect) input.tomselect.destroy();

  const preloadValue = input.value.trim();

  const tomSelect = new TomSelect(input, {
    valueField: 'id',
    labelField: 'name',
    searchField: ['search_text'],
    maxItems: 1,
    create: false,
    delimiter: '\x00',
    dropdownParent: 'body',
    shouldLoad: query => query.length >= 1,
    load(query, callback) {
      fetch(`/autocomplete?term=${encodeURIComponent(query)}`)
        .then(r => r.json())
        .then(items => callback(items.map(buildRemoteCardAutocompleteItem)))
        .catch(() => callback());
    },
    render: {
      option: renderCardItem,
      item: renderCardItem,
    },
    onType(query) {
      clearStaleTomSelectSelection(this, query);
    },
    onChange(value) {
      syncCommittedTomSelectSelection(input, this);
      finalizeSingleTomSelectSelection(this, value);
      if (submitOnSelect && value) {
        input.closest('form')?.submit();
      }
    }
  });
  disableTomSelectRemoteQueryCache(tomSelect);
  bindTomSelectSubmitSync(input);
  bindTomSelectClipboardSync(input);
  bindTomSelectQueryTracking(input);
  if (input.classList.contains('layout-search-input')) {
    tomSelect.dropdown.classList.add('navbar-search-dropdown');
    tomSelect.dropdown.dataset.bsTheme = 'light';
  }

  // Resolve preloaded value to full option data so the custom renderer fires.
  if (preloadValue) {
    resolvePastedCardSelection(preloadValue, input)
      .then(match => {
        if (match) {
          tomSelect.updateOption(preloadValue, match);
          tomSelect.setValue(match.id, true);
        }
      })
      .catch(() => {});
  }

  return tomSelect;
}

// Autocomplete for /autocomplete_card — simple string array of display names
export function initSimpleCardAutocomplete(input) {
  if (!isTomSelectElement(input)) return null;
  if (input.tomselect) input.tomselect.destroy();
  const tomSelect = new TomSelect(input, {
    valueField: 'value',
    labelField: 'label',
    searchField: ['search_text'],
    maxItems: 1,
    create: false,
    delimiter: '\x00',
    shouldLoad: query => query.length >= 1,
    load(query, callback) {
      fetch(`/autocomplete_card?term=${encodeURIComponent(query)}`)
        .then(r => r.json())
        .then(items => callback(items.map(s => ({
          value: s,
          label: s,
          search_text: buildAutocompleteSearchText([s]),
        }))))
        .catch(() => callback());
    },
    onType(query) {
      clearStaleTomSelectSelection(this, query);
    },
    onChange(value) {
      syncCommittedTomSelectSelection(input, this);
      finalizeSingleTomSelectSelection(this, value);
    }
  });
  disableTomSelectRemoteQueryCache(tomSelect);
  bindTomSelectSubmitSync(input);
  bindTomSelectClipboardSync(input);
  bindTomSelectQueryTracking(input);
  if (input.classList.contains('autocomplete-submit-current')) {
    bindTomSelectEnterSubmit(input);
  }
  return tomSelect;
}

// --- globals for legacy view callers ---

window.initCardIdAutocomplete = initCardIdAutocomplete;

export function bindAutoCompleteCard() {
  document.querySelectorAll('input.autocomplete-card, textarea.autocomplete-card, select.autocomplete-card').forEach(initSimpleCardAutocomplete);
}
window.bindAutoCompleteCard = bindAutoCompleteCard;

export function bindSearchAutoComplete(elements, _searchBox, _searchButton, submit) {
  if (!elements) return;
  Array.from(elements).forEach(el => initCardIdAutocomplete(el, { submitOnSelect: !!submit }));
}
window.bindSearchAutoComplete = bindSearchAutoComplete;

function bindCardAutoComplete() {
  document.querySelectorAll('input.autocomplete-card-id, textarea.autocomplete-card-id, select.autocomplete-card-id').forEach(el =>
    initCardIdAutocomplete(el)
  );
  document.querySelectorAll('input.autocomplete-card-id-submit, textarea.autocomplete-card-id-submit, select.autocomplete-card-id-submit').forEach(el =>
    initCardIdAutocomplete(el, { submitOnSelect: true })
  );
}
window.bindCardAutoComplete = bindCardAutoComplete;

document.addEventListener('DOMContentLoaded', () => {
  bindCardAutoComplete();
  bindAutoCompleteCard();

  const loginBtn = document.getElementById('layout-login-signup-btn');
  if (loginBtn) {
    loginBtn.addEventListener('click', () => {
      document.getElementById('layout-login-signup-form')?.submit();
    });
  }
});
